import React, { useState, useEffect } from "react";
import { useUpdateMainCenterStockMutation, useGetInventoriesQuery } from "../api/starterKitApi";
import { showToast } from "../../../core/utils/alert";
import { getApiErrorMessage } from "../../../core/utils/errorHandler";
import CommonSelect from "../../../core/common/commonSelect";
import { useCourse } from "../../configuration/course/hooks/useCourse";

const UpdateMainCenterStockModal = () => {
  const [formData, setFormData] = useState({
    course_id: "",
    total_stock: 0,
    available_stock: 0,
  });

  const { data: inventoryData } = useGetInventoriesQuery({ size: 100 });
  const [updateMainCenterStock, { isLoading }] = useUpdateMainCenterStockMutation();

  const { getCourseState } = useCourse();
  const { courses } = getCourseState();
  const courseOptions = courses.map(course => ({
    value: course.id.toString(),
    label: course.name,
  }));

  // Watch for course change and update formData with existing stock if available
  useEffect(() => {
    if (formData.course_id) {
      const mainCenterInventory = inventoryData?.data?.items.find((item: any) => 
        item.is_main_center_stock && (Number(item.course_id) === Number(formData.course_id) || Number(item.course?.id) === Number(formData.course_id))
      );
      if (mainCenterInventory) {
        setFormData(prev => ({
          ...prev,
          total_stock: mainCenterInventory.total_stock,
          available_stock: mainCenterInventory.available_stock,
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          total_stock: 0,
          available_stock: 0,
        }));
      }
    }
  }, [formData.course_id, inventoryData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.course_id) {
      showToast({ title: "Error", text: "Please select a course", icon: "error" });
      return;
    }

    if (formData.available_stock > formData.total_stock) {
      showToast({ title: "Error", text: "Available stock cannot exceed total stock", icon: "error" });
      return;
    }

    try {
      const response = await updateMainCenterStock({
        ...formData,
        course_id: Number(formData.course_id),
      }).unwrap();

      if (response.success) {
        showToast({
          title: "Success",
          text: "Main Center stock updated successfully",
          icon: "success",
        });

        // Close modal
        const modalElement = document.getElementById("update_main_center_stock");
        if (modalElement) {
          const bootstrap = (window as any).bootstrap;
          if (bootstrap && bootstrap.Modal) {
            const modal = bootstrap.Modal.getInstance(modalElement);
            modal?.hide();
          } else {
            const closeButton = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLButtonElement;
            closeButton?.click();
          }
        }
      }
    } catch (error: any) {
      console.error("Update error:", error);
      showToast({
        title: "Error",
        text: getApiErrorMessage(error) || "Failed to update stock",
        icon: "error",
      });
    }
  };

  return (
    <div className="modal fade" id="update_main_center_stock">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4 className="modal-title">Update Main Center Stock (Manual Override)</h4>
            <button
              type="button"
              className="btn-close custom-btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            >
              <i className="ti ti-x" />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="alert alert-warning">
                <i className="ti ti-alert-triangle me-2"></i>
                This action will manually override the current stock levels. Use with caution.
              </div>
              <div className="row">
                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label">Course</label>
                    <CommonSelect
                      options={courseOptions}
                      value={courseOptions.find(opt => opt.value === formData.course_id)}
                      onValueChange={(val) => setFormData({ ...formData, course_id: val })}
                      placeholder="Select Course"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Total Stock</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.total_stock}
                      onChange={(e) => setFormData({
                        ...formData,
                        total_stock: Number(e.target.value)
                      })}
                      required
                      min="0"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Available Stock</label>
                    <input
                      type="number"
                      className="form-control"
                      value={formData.available_stock}
                      onChange={(e) => setFormData({
                        ...formData,
                        available_stock: Number(e.target.value)
                      })}
                      required
                      min="0"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="modal"
              >
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Stock"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateMainCenterStockModal;
